import React, { useState, useEffect, useCallback, useRef } from 'react';
import ClusterAssignmentCard from './ClusterAssignmentCard';
import BehavioralFeaturesCard from './BehavioralFeaturesCard';
import ClusterVisualizationChart from './ClusterVisualizationChart';
import {
  getBehavioralFeatures,
  getClusterAssignment,
  getClusterVisualization,
  getAllClusterProfiles,
  getClusterLabelConfig,
  trainClusterModel,
} from '../../../services/patternClustering.service';
import mainApiClient from '../../../services/interceptor.mainApiClient';
import { normalizePeopleResponse } from '../../../utils/responseNormalizer';
import { isDemoMode } from '../../../services/demoMode';
import { getDemoPeopleList } from '../../../services/demoData.service';

/**
 * PatternClusteringPanel - Main composite panel for pattern clustering visualization
 */
const PatternClusteringPanel = ({
  alienId,
  showVisualization = true,
  showFeatures = true,
  compact = false,
}) => {
  const [features, setFeatures] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [visualization, setVisualization] = useState(null);
  const [profiles, setProfiles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Training state
  const [isTraining, setIsTraining] = useState(false);
  const [trainingResult, setTrainingResult] = useState(null);
  const [nClusters, setNClusters] = useState(5);
  const [trainingDays, setTrainingDays] = useState(30);
  const [selectedPersonIds, setSelectedPersonIds] = useState([]);

  // Subject picker state
  const [people, setPeople] = useState([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Load people list for subject picker
  const loadPeople = useCallback(async () => {
    setPeopleLoading(true);
    try {
      if (isDemoMode()) {
        setPeople(getDemoPeopleList());
      } else {
        const response = await mainApiClient.get('people/aliens', { params: { page: 1, pageSize: 10000 } });
        const result = normalizePeopleResponse(response.data);
        setPeople(Array.isArray(result) ? result : []);
      }
    } catch (err) {
      console.error('[PatternClusteringPanel] Failed to load people:', err);
      setPeople([]);
    } finally {
      setPeopleLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPeople();
  }, [loadPeople]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const togglePersonId = (id) => {
    setSelectedPersonIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const selectAllPeople = () => {
    setSelectedPersonIds(people.map((p) => p.alienId ?? p.peopleId));
  };

  const clearAllPeople = () => {
    setSelectedPersonIds([]);
  };

  // Fetch all clustering data
  const fetchClusteringData = useCallback(async () => {
    if (!alienId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch features and assignment for this person
      const [featuresResult, assignmentResult] = await Promise.all([
        showFeatures ? getBehavioralFeatures(alienId) : Promise.resolve(null),
        getClusterAssignment(alienId),
      ]);

      setFeatures(featuresResult);
      setAssignment(assignmentResult);

      // Fetch visualization and profiles if needed
      if (showVisualization) {
        const [vizResult, profilesResult] = await Promise.all([
          getClusterVisualization(100),
          getAllClusterProfiles(),
        ]);
        setVisualization(vizResult);
        setProfiles(profilesResult);
      }
    } catch (err) {
      console.error('[PatternClusteringPanel] Error fetching data:', err);
      setError('Failed to load clustering data');
    } finally {
      setLoading(false);
    }
  }, [alienId, showFeatures, showVisualization]);

  // Initial load
  useEffect(() => {
    fetchClusteringData();
  }, [fetchClusteringData]);

  // Handle refresh
  const handleRefresh = () => {
    fetchClusteringData();
  };

  // Handle training
  const handleTrainModel = async () => {
    setIsTraining(true);
    setTrainingResult(null);
    setError(null);

    try {
      if (selectedPersonIds.length < 2) {
        setError('Please select at least 2 subjects');
        setIsTraining(false);
        return;
      }

      const result = await trainClusterModel(nClusters, trainingDays, selectedPersonIds);
      if (result) {
        setTrainingResult(result);
        // Refresh data after successful training
        if (result.status === 'success' || result.nSubjects > 0) {
          await fetchClusteringData();
        }
      } else {
        setError('Training failed. Check if ML Functions service is running.');
      }
    } catch (err) {
      console.error('[PatternClusteringPanel] Training error:', err);
      setError(err.message || 'Failed to train clustering model');
    } finally {
      setIsTraining(false);
    }
  };

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">Pattern Clustering</h3>
          <button
            onClick={handleRefresh}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Retry
          </button>
        </div>
        <div className="text-center py-4 text-red-500 text-sm">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Compact layout
  if (compact) {
    return (
      <ClusterAssignmentCard assignment={assignment} loading={loading} />
    );
  }

  // Full layout
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">
          Behavioral Pattern Clustering
        </h2>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Training Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Train Clustering Model</h3>
        <p className="text-xs text-gray-500 mb-4">
          Train the K-Means clustering model on all subjects with location data. This must be done before visualization data is available.
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px] relative" ref={dropdownRef}>
            <label className="block text-xs font-medium text-gray-600 mb-1">Subjects</label>
            <button
              type="button"
              onClick={() => setShowDropdown((v) => !v)}
              className="w-full px-2 py-1.5 text-sm border rounded-md text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center justify-between"
            >
              <span className={selectedPersonIds.length === 0 ? 'text-gray-400' : 'text-gray-800'}>
                {peopleLoading
                  ? 'Loading subjects...'
                  : selectedPersonIds.length === 0
                    ? 'Select subjects...'
                    : `${selectedPersonIds.length} subject${selectedPersonIds.length !== 1 ? 's' : ''} selected`}
              </span>
              <svg className={`w-4 h-4 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showDropdown && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                  <span className="text-xs text-gray-500">{people.length} subjects available</span>
                  <div className="flex gap-2">
                    <button type="button" onClick={selectAllPeople} className="text-xs text-indigo-600 hover:text-indigo-800">Select All</button>
                    <span className="text-gray-300">|</span>
                    <button type="button" onClick={clearAllPeople} className="text-xs text-indigo-600 hover:text-indigo-800">Clear All</button>
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {people.length === 0 && !peopleLoading && (
                    <div className="px-3 py-2 text-xs text-gray-400">No subjects found</div>
                  )}
                  {people.map((person) => {
                    const id = person.alienId ?? person.peopleId;
                    const name = person.alienName || person.peopleName || `${person.firstName || ''} ${person.lastName || ''}`.trim();
                    const checked = selectedPersonIds.includes(id);
                    return (
                      <label
                        key={id}
                        className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-indigo-50 text-sm ${checked ? 'bg-indigo-50' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePersonId(id)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-gray-800">{name}</span>
                        <span className="text-gray-400 text-xs">({id})</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Clusters</label>
            <input
              type="number"
              min="2"
              max="10"
              value={nClusters}
              onChange={(e) => setNClusters(parseInt(e.target.value, 10) || 5)}
              className="w-20 px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Days</label>
            <input
              type="number"
              min="7"
              max="90"
              value={trainingDays}
              onChange={(e) => setTrainingDays(parseInt(e.target.value, 10) || 30)}
              className="w-20 px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            onClick={handleTrainModel}
            disabled={isTraining}
            className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isTraining ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                Training...
              </span>
            ) : (
              'Train Model'
            )}
          </button>
        </div>

        {/* Training Result */}
        {trainingResult && (
          <div className={`mt-4 p-3 rounded-lg ${
            trainingResult.nSubjects > 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={trainingResult.nSubjects > 0 ? 'text-green-600' : 'text-red-600'}>
                {trainingResult.nSubjects > 0 ? '✓' : '✗'}
              </span>
              <span className={`font-medium text-sm ${trainingResult.nSubjects > 0 ? 'text-green-800' : 'text-red-800'}`}>
                {trainingResult.nSubjects > 0 ? 'Training Successful' : 'Training Failed'}
              </span>
            </div>
            {trainingResult.nSubjects > 0 && (
              <div className="grid grid-cols-3 gap-4 text-xs">
                <div>
                  <span className="text-gray-500">Subjects:</span>{' '}
                  <span className="font-medium">{trainingResult.nSubjects}</span>
                </div>
                <div>
                  <span className="text-gray-500">Clusters:</span>{' '}
                  <span className="font-medium">{trainingResult.nClusters}</span>
                </div>
                {trainingResult.modelMetrics && (
                  <div>
                    <span className="text-gray-500">Silhouette:</span>{' '}
                    <span className="font-medium">{trainingResult.modelMetrics.silhouetteScore?.toFixed(3) || 'N/A'}</span>
                  </div>
                )}
              </div>
            )}
            {trainingResult.errors && trainingResult.errors.length > 0 && (
              <div className="mt-2 text-xs text-orange-600">
                {trainingResult.errors.length} subjects had errors during processing
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main content grid */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Left column: Assignment + Features */}
        <div className="space-y-4">
          <ClusterAssignmentCard assignment={assignment} loading={loading} />
          {showFeatures && (
            <BehavioralFeaturesCard features={features} loading={loading} />
          )}
        </div>

        {/* Right columns: Visualization + Profiles */}
        {showVisualization && (
          <div className="lg:col-span-2 space-y-4">
            <ClusterVisualizationChart
              visualization={visualization}
              loading={loading}
              highlightPersonId={alienId ? parseInt(alienId, 10) : null}
              height={350}
            />

            {/* Cluster Profiles Summary */}
            {profiles && profiles.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  All Cluster Profiles
                </h3>
                <div className="grid grid-cols-5 gap-2">
                  {profiles.map((profile) => {
                    const config = getClusterLabelConfig(profile.clusterLabel);
                    const isCurrentCluster = assignment?.clusterId === profile.clusterId;
                    return (
                      <div
                        key={profile.clusterId}
                        className={`p-2 rounded border text-center ${
                          isCurrentCluster
                            ? 'border-2 border-blue-500 bg-blue-50'
                            : 'border-gray-200'
                        }`}
                      >
                        <div
                          className="w-8 h-8 rounded-full mx-auto mb-1 flex items-center justify-center text-white font-bold text-sm"
                          style={{ backgroundColor: config.color }}
                        >
                          {profile.clusterId}
                        </div>
                        <div className="text-xs font-medium truncate" title={config.label}>
                          {config.label}
                        </div>
                        <div className="text-xs text-gray-500">
                          {profile.memberCount} members
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info banner */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <span className="text-indigo-500 text-lg">&#x1F4CA;</span>
          <div className="text-xs text-indigo-700">
            <p className="font-medium mb-1">About Pattern Clustering</p>
            <p>
              This analysis groups subjects with similar behavioral patterns using K-Means clustering
              on 8 behavioral features: daily distance, location diversity, routine adherence,
              night/weekend activity ratios, high-risk zone affinity, velocity variance, and dwell time entropy.
              Subjects in the same cluster exhibit similar movement and behavioral patterns.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatternClusteringPanel;
